/**
 * Universitäts-Daten für die Seite /unis-in-deutschland/.
 *
 * Statischer Export: Es gibt kein Backend, daher leben die Daten als
 * typisiertes Modul hier. Die Struktur ist bewusst API-nah gehalten,
 * damit sie später 1:1 durch einen Fetch ersetzt werden kann.
 */

export type QuoteKey = "abq" | "adh" | "zeq";

export type AdmissionCriterion = {
  name: string;
  quotes: QuoteKey[];
};

export type Semester = "Wintersemester" | "Sommersemester";
export type UniType = "Staatlich" | "Privat";
export type UniLanguage = "Deutsch" | "Englisch";
export type Subject = "Humanmedizin" | "Zahnmedizin";

export type University = {
  id: string;
  name: string;
  city: string;
  state: string;
  type: UniType;
  semesters: Semester[];
  subjects: Subject[];
  language: UniLanguage;
  /** Anzeigetext der Studiengebühren; null = gebührenfrei (nur Semesterbeitrag). */
  tuition: string | null;
  /** Studienplätze pro Jahr, sofern bekannt (v. a. private Hochschulen). */
  seats?: number;
  /** Auswahlkriterien je Quote – nur für staatliche Hochschulen. */
  criteria?: AdmissionCriterion[];
};

const crit = (name: string, ...quotes: QuoteKey[]): AdmissionCriterion => ({
  name,
  quotes,
});

const WINTER: Semester[] = ["Wintersemester"];
const BOTH: Semester[] = ["Wintersemester", "Sommersemester"];

export const universities: University[] = [
  {
    id: "charite-berlin",
    name: "Charité – Universitätsmedizin Berlin",
    city: "Berlin",
    state: "Berlin",
    type: "Staatlich",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh"),
      crit("Berufsausbildung", "zeq"),
      crit("Berufstätigkeit", "zeq"),
    ],
  },
  {
    id: "uni-kiel",
    name: "Christian-Albrechts-Universität zu Kiel (CAU)",
    city: "Kiel",
    state: "Schleswig-Holstein",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Wettbewerb", "zeq"),
      crit("Berufstätigkeit", "zeq"),
    ],
  },
  {
    id: "mcn-niederbayern",
    name: "MedizinCampus Niederbayern (MCN)",
    city: "Regensburg",
    state: "Bayern",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Auswahlgespräch MCN", "adh", "zeq"),
    ],
  },
  {
    id: "uni-tuebingen",
    name: "Eberhard Karls Universität Tübingen",
    city: "Tübingen",
    state: "Baden-Württemberg",
    type: "Staatlich",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Berufstätigkeit", "zeq"),
    ],
  },
  {
    id: "fau-bayreuth",
    name: "FAU Erlangen-Nürnberg / Bayreuth",
    city: "Bayreuth",
    state: "Bayern",
    type: "Staatlich",
    semesters: BOTH,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "fau-erlangen",
    name: "Friedrich-Alexander-Universität Erlangen-Nürnberg (FAU)",
    city: "Erlangen",
    state: "Bayern",
    type: "Staatlich",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-jena",
    name: "Friedrich-Schiller-Universität Jena",
    city: "Jena",
    state: "Thüringen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "zeq"),
      crit("Wettbewerb", "zeq"),
    ],
  },
  {
    id: "uni-goettingen",
    name: "Georg-August-Universität Göttingen",
    city: "Göttingen",
    state: "Niedersachsen",
    type: "Staatlich",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-frankfurt",
    name: "Goethe-Universität Frankfurt",
    city: "Frankfurt am Main",
    state: "Hessen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Berufstätigkeit", "zeq"),
    ],
  },
  {
    id: "uni-duesseldorf",
    name: "Heinrich-Heine-Universität Düsseldorf",
    city: "Düsseldorf",
    state: "Nordrhein-Westfalen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-mainz",
    name: "Johannes Gutenberg-Universität Mainz",
    city: "Mainz",
    state: "Rheinland-Pfalz",
    type: "Staatlich",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Berufstätigkeit", "adh", "zeq"),
    ],
  },
  {
    id: "uni-wuerzburg",
    name: "Julius-Maximilians-Universität Würzburg",
    city: "Würzburg",
    state: "Bayern",
    type: "Staatlich",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "zeq"),
    ],
  },
  {
    id: "uni-giessen",
    name: "Justus-Liebig-Universität Gießen",
    city: "Gießen",
    state: "Hessen",
    type: "Staatlich",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Berufstätigkeit", "adh", "zeq"),
    ],
  },
  {
    id: "kassel-school-of-medicine",
    name: "Kassel School of Medicine",
    city: "Kassel",
    state: "Hessen",
    type: "Privat",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Englisch",
    tuition: "19.500 € / Jahr",
  },
  {
    id: "lmu-muenchen",
    name: "Ludwig-Maximilians-Universität München",
    city: "München",
    state: "Bayern",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-halle",
    name: "Martin-Luther-Universität Halle-Wittenberg",
    city: "Halle (Saale)",
    state: "Sachsen-Anhalt",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "medic-dresden-chemnitz",
    name: "MEDiC – TU Dresden / Chemnitz",
    city: "Chemnitz",
    state: "Sachsen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Interview Dresden/Chemnitz", "adh", "zeq"),
      crit("Berufsausbildung", "zeq"),
    ],
  },
  {
    id: "mhb-brandenburg",
    name: "Medizinische Hochschule Brandenburg Theodor Fontane (MHB)",
    city: "Neuruppin",
    state: "Brandenburg",
    type: "Privat",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: "1.967 € / Monat",
  },
  {
    id: "mhh-hannover",
    name: "Medizinische Hochschule Hannover (MHH)",
    city: "Hannover",
    state: "Niedersachsen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-magdeburg",
    name: "Otto-von-Guericke-Universität Magdeburg",
    city: "Magdeburg",
    state: "Sachsen-Anhalt",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("HAM-Nat", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Berufstätigkeit", "zeq"),
    ],
  },
  {
    id: "pmu-nuernberg",
    name: "Paracelsus Medizinische Privatuniversität Nürnberg",
    city: "Nürnberg",
    state: "Bayern",
    type: "Privat",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: "11.000 € / Semester",
    seats: 55,
  },
  {
    id: "uni-marburg",
    name: "Philipps-Universität Marburg",
    city: "Marburg",
    state: "Hessen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "zeq"),
      crit("Wettbewerb", "zeq"),
      crit("Berufstätigkeit", "zeq"),
    ],
  },
  {
    id: "uni-bonn",
    name: "Rheinische Friedrich-Wilhelms-Universität Bonn",
    city: "Bonn",
    state: "Nordrhein-Westfalen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "rub-bochum",
    name: "Ruhr-Universität Bochum (RUB)",
    city: "Bochum",
    state: "Nordrhein-Westfalen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-heidelberg",
    name: "Ruprecht-Karls-Universität Heidelberg",
    city: "Heidelberg",
    state: "Baden-Württemberg",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Berufstätigkeit", "adh", "zeq"),
      crit("IKM Heidelberg", "zeq"),
    ],
  },
  {
    id: "rwth-aachen",
    name: "RWTH Aachen",
    city: "Aachen",
    state: "Nordrhein-Westfalen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "tu-dresden",
    name: "Technische Universität Dresden",
    city: "Dresden",
    state: "Sachsen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Interview Dresden", "adh", "zeq"),
    ],
  },
  {
    id: "uni-augsburg",
    name: "Universität Augsburg",
    city: "Augsburg",
    state: "Bayern",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-bielefeld",
    name: "Universität Bielefeld",
    city: "Bielefeld",
    state: "Nordrhein-Westfalen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufstätigkeit", "adh", "zeq"),
    ],
  },
  {
    id: "uni-saarland",
    name: "Universität des Saarlandes",
    city: "Homburg",
    state: "Saarland",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-duisburg-essen",
    name: "Universität Duisburg-Essen",
    city: "Essen",
    state: "Nordrhein-Westfalen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "zeq"),
    ],
  },
  {
    id: "uni-greifswald",
    name: "Universität Greifswald",
    city: "Greifswald",
    state: "Mecklenburg-Vorpommern",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Interview Greifswald", "adh", "zeq"),
    ],
  },
  {
    id: "uni-hamburg",
    name: "Universität Hamburg",
    city: "Hamburg",
    state: "Hamburg",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("HAM-Nat", "adh", "zeq"),
      crit("HAM-SJT", "adh", "zeq"),
    ],
  },
  {
    id: "uni-mannheim-medizin",
    name: "Universität Heidelberg – Medizinische Fakultät Mannheim",
    city: "Mannheim",
    state: "Baden-Württemberg",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Berufstätigkeit", "adh", "zeq"),
    ],
  },
  {
    id: "uni-leipzig",
    name: "Universität Leipzig",
    city: "Leipzig",
    state: "Sachsen",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-regensburg",
    name: "Universität Regensburg",
    city: "Regensburg",
    state: "Bayern",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-rostock",
    name: "Universität Rostock",
    city: "Rostock",
    state: "Mecklenburg-Vorpommern",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
    ],
  },
  {
    id: "uni-ulm",
    name: "Universität Ulm",
    city: "Ulm",
    state: "Baden-Württemberg",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
      crit("Berufstätigkeit", "zeq"),
    ],
  },
  {
    id: "uni-witten-herdecke",
    name: "Universität Witten/Herdecke",
    city: "Witten",
    state: "Nordrhein-Westfalen",
    type: "Privat",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: "1.220 € / Monat",
    seats: 168,
  },
  {
    id: "uni-koeln",
    name: "Universität zu Köln",
    city: "Köln",
    state: "Nordrhein-Westfalen",
    type: "Staatlich",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Berufsausbildung", "adh", "zeq"),
    ],
  },
  {
    id: "uni-luebeck",
    name: "Universität zu Lübeck",
    city: "Lübeck",
    state: "Schleswig-Holstein",
    type: "Staatlich",
    semesters: WINTER,
    subjects: ["Humanmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "zeq"),
    ],
  },
  {
    id: "umch-hamburg",
    name: "Universitätsmedizin Neumarkt a. M. Campus Hamburg (UMCH)",
    city: "Hamburg",
    state: "Hamburg",
    type: "Privat",
    semesters: BOTH,
    subjects: ["Humanmedizin"],
    language: "Englisch",
    tuition: "14.900 € / Semester",
    seats: 150,
  },
  {
    id: "uni-muenster",
    name: "Westfälische Wilhelms-Universität Münster",
    city: "Münster",
    state: "Nordrhein-Westfalen",
    type: "Staatlich",
    semesters: BOTH,
    subjects: ["Humanmedizin", "Zahnmedizin"],
    language: "Deutsch",
    tuition: null,
    criteria: [
      crit("Abitur", "abq", "adh"),
      crit("TMS", "adh", "zeq"),
      crit("Dienst/Ehrenamt", "adh", "zeq"),
      crit("Wettbewerb", "adh", "zeq"),
      crit("Berufsausbildung", "zeq"),
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Filter                                                              */
/* ------------------------------------------------------------------ */

export type UniFilters = {
  search: string;
  states: string[];
  subjects: string[];
  types: string[];
  semesters: string[];
  languages: string[];
  /** "" = alle, "free" = gebührenfrei, "paid" = kostenpflichtig */
  tuition: "" | "free" | "paid";
};

export const EMPTY_FILTERS: UniFilters = {
  search: "",
  states: [],
  subjects: [],
  types: [],
  semesters: [],
  languages: [],
  tuition: "",
};

export function countActiveFilters(filters: UniFilters): number {
  return (
    filters.states.length +
    filters.subjects.length +
    filters.types.length +
    filters.semesters.length +
    filters.languages.length +
    (filters.tuition ? 1 : 0)
  );
}

export function filterUniversities(
  unis: University[],
  filters: UniFilters
): University[] {
  const query = filters.search.trim().toLowerCase();

  return unis.filter((uni) => {
    if (query) {
      const haystack = `${uni.name} ${uni.city} ${uni.state}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    if (filters.states.length && !filters.states.includes(uni.state)) return false;
    if (
      filters.subjects.length &&
      !filters.subjects.some((subject) => uni.subjects.includes(subject as Subject))
    )
      return false;
    if (filters.types.length && !filters.types.includes(uni.type)) return false;
    if (
      filters.semesters.length &&
      !filters.semesters.some((semester) =>
        uni.semesters.includes(semester as Semester)
      )
    )
      return false;
    if (filters.languages.length && !filters.languages.includes(uni.language))
      return false;
    if (filters.tuition === "free" && uni.tuition !== null) return false;
    if (filters.tuition === "paid" && uni.tuition === null) return false;
    return true;
  });
}

/** Alphabetisch sortierte Bundesländer, abgeleitet aus den Daten. */
export const allStates: string[] = [...new Set(universities.map((u) => u.state))].sort(
  (a, b) => a.localeCompare(b, "de")
);

export const allSubjects: Subject[] = ["Humanmedizin", "Zahnmedizin"];
export const allTypes: UniType[] = ["Staatlich", "Privat"];
export const allSemesters: Semester[] = ["Wintersemester", "Sommersemester"];
export const allLanguages: UniLanguage[] = ["Deutsch", "Englisch"];

/** Kartenbild – Dateien liegen unter public/images/unis/<id>.webp. */
export function uniImagePath(id: string): string {
  return `/images/unis/${id}.webp`;
}

/** Route der Detailseite einer Universität. */
export function uniDetailPath(id: string): string {
  return `/unis-in-deutschland/${id}/`;
}

export function semesterLabel(semesters: Semester[]): string {
  return semesters.length === 2 ? "Winter- & Sommersemester" : semesters[0];
}

/** Kurzbeschreibung für Karte & SEO – aus den Felddaten abgeleitet. */
export function uniSummary(uni: University): string {
  const subjectText = uni.subjects.join(" und ");
  const base =
    uni.type === "Staatlich"
      ? `Staatliche Hochschule in ${uni.city} ohne Studiengebühren.`
      : `Private Hochschule in ${uni.city} mit eigenem Auswahlverfahren.`;
  return `${base} ${subjectText} zum ${semesterLabel(uni.semesters)}.`;
}

/* ------------------------------------------------------------------ */
/* FAQ für die Uni-Liste                                               */
/* ------------------------------------------------------------------ */

export const uniFaqs = [
  {
    question: "Wie viele Universitäten bieten in Deutschland Medizin an?",
    answer:
      "In Deutschland kannst du an rund 40 staatlichen Hochschulen sowie an mehreren privaten Universitäten Humanmedizin studieren. Viele Standorte bieten zusätzlich Zahnmedizin an. Die Übersicht auf dieser Seite zeigt dir alle Standorte inklusive Bundesland, Semesterbeginn und Auswahlkriterien.",
  },
  {
    question: "Was bedeuten ABQ, AdH und ZEQ?",
    answer:
      "Die Studienplätze an staatlichen Hochschulen werden über drei Quoten vergeben: In der Abiturbestenquote (ABQ, 30 %) zählt nur dein Abitur. Im Auswahlverfahren der Hochschulen (AdH, 60 %) kombiniert jede Uni eigene Kriterien wie Abitur, TMS oder Berufsausbildung. In der Zusätzlichen Eignungsquote (ZEQ, 10 %) spielt das Abitur keine Rolle – hier zählen Kriterien wie TMS, Dienste oder eine abgeschlossene Ausbildung.",
  },
  {
    question: "Was kostet das Medizinstudium in Deutschland?",
    answer:
      "An staatlichen Hochschulen fallen keine Studiengebühren an – du zahlst lediglich den Semesterbeitrag deiner Hochschule. Private Universitäten erheben dagegen Studiengebühren, die je nach Standort zwischen etwa 1.200 € pro Monat und 19.500 € pro Jahr liegen können.",
  },
  {
    question: "Worin unterscheiden sich staatliche und private Hochschulen?",
    answer:
      "Staatliche Hochschulen vergeben ihre Studienplätze zentral über hochschulstart.de nach den Quoten ABQ, AdH und ZEQ – dein Abitur und Tests wie der TMS sind hier entscheidend. Private Hochschulen führen eigene Auswahlverfahren durch, bei denen oft Motivation, Interviews und Auswahltage im Vordergrund stehen. Dafür fallen Studiengebühren an.",
  },
  {
    question: "Wie finde ich heraus, an welchen Unis meine Werte reichen?",
    answer:
      "Mit der kostenlosen ncrechner App gibst du einfach deine Werte ein – Abiturnote, TMS, HamNat und mehr – und siehst in Echtzeit, an welchen Hochschulen eine Zulassung wahrscheinlich gewesen wäre. Inklusive Prognose für das kommende Semester.",
  },
];
